import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Gift, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const COLORS = ["#f43f5e", "#ec4899", "#f59e0b", "#10b981", "#8b5cf6", "#06b6d4"];
const seenKey = () => `itz_today_done_${new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" })}`;

export const NextRewardProgress = ({ next, points, compact }) => next ? (
  <Link to="/geschenke" data-testid="next-reward" className={`block shine glow-pulse rounded-2xl bg-white/80 border border-rose-100 hover:border-rose-300 transition-colors ${compact ? "px-3 py-2" : "p-3"}`} style={{ "--shine-delay": "1.5s" }}>
    <div className="flex items-center gap-2 text-xs">
      <Gift className="w-4 h-4 text-rose-500 shrink-0" />
      <span className="font-semibold truncate flex-1">Nächste Prämie: <b>{next.title}</b></span>
      <span className="font-bold text-rose-600 whitespace-nowrap" data-testid="next-reward-missing">noch {next.missing.toLocaleString("de-DE")}</span>
    </div>
    <div className="mt-1.5 h-2 rounded-full bg-rose-50 overflow-hidden"><div className="h-full pink-gradient transition-[width] duration-700" style={{ width: `${Math.min(100, next.pct)}%` }} /></div>
    {!compact && <p className="mt-1 text-[11px] text-slate-400">{points.toLocaleString("de-DE")} von {next.cost.toLocaleString("de-DE")} Punkten</p>}
  </Link>
) : null;

const Confetti = () => (
  <span className="pointer-events-none absolute right-6 top-1/2" aria-hidden data-testid="today-confetti">
    {Array.from({ length: 14 }).map((_, i) => (
      <i key={i} className="today-confetti" style={{ "--a": `${(i / 14) * 360}deg`, "--d": `${26 + (i % 3) * 10}px`, background: COLORS[i % COLORS.length], animationDelay: `${(i % 4) * 30}ms` }} />
    ))}
  </span>
);

function Item({ it, fresh }) {
  return (
    <li className="relative">
      <Link to={it.url} data-testid={`today-item-${it.id}`} data-done={it.done ? "true" : "false"}
        className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 transition-colors ${it.done ? "bg-emerald-50/60 border-emerald-100" : "bg-white border-rose-50 hover:border-rose-200"}`}>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-bold truncate ${it.done ? "text-slate-400 line-through decoration-emerald-400 decoration-2" : ""}`}>{it.title}</p>
          <p className="text-xs text-slate-500 truncate">{it.done ? "Erledigt – super gemacht!" : it.text}</p>
        </div>
        {it.done ? (
          <span className={`w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-emerald-200 ${fresh ? "today-check-pop" : ""}`} data-testid={`today-check-${it.id}`}><Check className="w-4 h-4" strokeWidth={3} /></span>
        ) : (
          <>
            {it.points ? <span className="text-xs font-extrabold text-rose-600 whitespace-nowrap">+{it.points}</span> : null}
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
          </>
        )}
      </Link>
      {fresh && <Confetti />}
    </li>
  );
}

export function TodayForYou() {
  const [d, setD] = useState(null);
  const [fresh, setFresh] = useState([]);
  useEffect(() => {
    const load = () => api.get("/today").then(({ data }) => {
      const seen = JSON.parse(localStorage.getItem(seenKey()) || "null");
      const done = data.items.filter((i) => i.done).map((i) => i.id);
      if (seen) {
        const add = done.filter((id) => !seen.includes(id));
        if (add.length) { setFresh((f) => [...new Set([...f, ...add])]); setTimeout(() => setFresh((f) => f.filter((id) => !add.includes(id))), 1600); }
      }
      localStorage.setItem(seenKey(), JSON.stringify(done));
      setD(data);
    }).catch(() => {});
    load();
    window.addEventListener("points-awarded", load);
    return () => window.removeEventListener("points-awarded", load);
  }, []);
  if (!d) return null;
  const total = d.items.length;
  return (
    <section className="glow-card rounded-3xl bg-gradient-to-br from-rose-50 via-white to-pink-50 border border-rose-100 p-4 sm:p-5 space-y-3" data-testid="today-for-you">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-rose-500" />
        <h2 className="font-display text-lg font-extrabold flex-1">Heute für dich</h2>
        <span className="text-xs font-extrabold text-emerald-600" data-testid="today-progress">{d.done}/{total} erledigt</span>
        <Link to="/punkte-sammeln" data-testid="today-guide-link" className="text-xs font-bold text-rose-600 hover:underline">Alle Wege</Link>
      </div>
      <div className="h-1.5 rounded-full bg-rose-50 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-[width] duration-700" style={{ width: `${(d.done / total) * 100}%` }} /></div>
      <ul className="space-y-1.5">{d.items.map((it) => <Item key={it.id} it={it} fresh={fresh.includes(it.id)} />)}</ul>
      {d.done === total && <p className="text-center text-sm font-bold text-emerald-600" data-testid="today-all-done">Alles erledigt für heute! 🎉 Morgen gibt's neue Chancen.</p>}
      <NextRewardProgress next={d.next} points={d.points} />
    </section>
  );
}
